import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, ArrowRight, Info } from 'lucide-react';
type ServiceType = 'house-clearance' | 'office-removals' | 'garden-waste' | 'commercial-waste' | 'overseas' | 'single-item';
const serviceBaseRates: Record<ServiceType, [number, number]> = {
  'house-clearance': [150, 250],
  'office-removals': [200, 350],
  'garden-waste': [80, 150],
  'commercial-waste': [80, 180],
  overseas: [500, 1000],
  'single-item': [40, 80]
};
const volumeMultipliers: Record<string, number> = {
  '1-5-bags': 1,
  'half-van': 1.5,
  'full-van': 2.5,
  '2-vans': 4,
  '3-plus': 6
};
const accessMultipliers: Record<string, number> = {
  easy: 1,
  moderate: 1.2,
  difficult: 1.5
};
interface InlineCostCalculatorProps {
  defaultService: ServiceType;
  serviceName: string;
}
export function InlineCostCalculator({
  defaultService,
  serviceName
}: InlineCostCalculatorProps) {
  const [volume, setVolume] = useState('');
  const [access, setAccess] = useState('easy');
  const [sameDay, setSameDay] = useState(false);
  const calculateEstimate = (): [number, number] | null => {
    if (!volume) return null;
    const [baseMin, baseMax] = serviceBaseRates[defaultService];
    const volMult = volumeMultipliers[volume] || 1;
    const accMult = accessMultipliers[access] || 1;
    const sameDayMult = sameDay ? 1.25 : 1;
    const min = Math.round(baseMin * volMult * accMult * sameDayMult / 10) * 10;
    const max = Math.round(baseMax * volMult * accMult * sameDayMult / 10) * 10;
    return [min, max];
  };
  const estimate = calculateEstimate();
  return <section className="py-16 px-4 bg-gray-50" data-id="element-52">
      <div className="max-w-3xl mx-auto" data-id="element-53">
        <div className="text-center mb-8" data-id="element-54">
          <div className="flex items-center justify-center gap-2 mb-3" data-id="element-55">
            <Calculator className="w-6 h-6" style={{
            color: '#1B8F4B'
          }} data-id="element-56" />
            <h2 className="text-2xl font-bold text-gray-900" style={{
            fontFamily: 'Montserrat, sans-serif'
          }} data-id="element-57">
              Quick Cost Estimator
            </h2>
          </div>
          <p className="text-gray-500 text-sm" data-id="element-58">
            Get an instant estimate for your {serviceName.toLowerCase()} job.
          </p>
        </div>

        <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100 space-y-6" data-id="element-59">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-60">
            <div data-id="element-61">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-62">
                Volume / Job Size
              </label>
              <select value={volume} onChange={e => setVolume(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-[#1B8F4B] focus:ring-2 focus:ring-[#1B8F4B]/20 transition-colors text-sm bg-white" data-id="element-63">
                <option value="" data-id="element-64">Select volume...</option>
                <option value="1-5-bags" data-id="element-65">1–5 bags / small load</option>
                <option value="half-van" data-id="element-66">Half van load</option>
                <option value="full-van" data-id="element-67">Full van load</option>
                <option value="2-vans" data-id="element-68">2 van loads</option>
                <option value="3-plus" data-id="element-69">3+ van loads / lorry</option>
              </select>
            </div>
            <div data-id="element-70">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-71">
                Access Difficulty
              </label>
              <select value={access} onChange={e => setAccess(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-[#1B8F4B] focus:ring-2 focus:ring-[#1B8F4B]/20 transition-colors text-sm bg-white" data-id="element-72">
                <option value="easy" data-id="element-73">Easy (ground floor / drive)</option>
                <option value="moderate" data-id="element-74">Moderate (stairs / narrow)</option>
                <option value="difficult" data-id="element-75">Difficult (no lift / tight)</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-4" data-id="element-76">
            <label className="text-sm font-semibold text-gray-700" data-id="element-77">
              Same-day service?
            </label>
            <div className="flex gap-2" data-id="element-78">
              {[{
              label: 'No',
              val: false
            }, {
              label: 'Yes (+25%)',
              val: true
            }].map(({
              label,
              val
            }) => <button key={label} onClick={() => setSameDay(val)} className="px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 border-2" style={{
              borderColor: sameDay === val ? '#1B8F4B' : '#E5E7EB',
              backgroundColor: sameDay === val ? '#F0FDF4' : 'white',
              color: sameDay === val ? '#1B8F4B' : '#6B7280'
            }} data-id="element-79">
                  {label}
                </button>)}
            </div>
          </div>

          {estimate ? <div className="rounded-xl p-5 text-center" style={{
          background: 'linear-gradient(135deg, #1B8F4B 0%, #0F5D2E 100%)'
        }} data-id="element-80">
              <p className="text-green-200 text-xs font-semibold mb-1" data-id="element-81">
                Estimated Price Range
              </p>
              <p className="text-3xl font-extrabold text-white mb-1" style={{
            fontFamily: 'Montserrat, sans-serif'
          }} data-id="element-82">
                £{estimate[0]} – £{estimate[1]}
              </p>
              <div className="flex items-center justify-center gap-1 text-green-200 text-xs mb-4" data-id="element-83">
                <Info className="w-3 h-3" data-id="element-84" /> Final quote after photos or site
                visit
              </div>
              <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-[#1B8F4B] bg-white hover:bg-green-50 transition-colors text-sm" style={{
            fontFamily: 'Montserrat, sans-serif'
          }} data-id="element-85">
                Send Details for Exact Quote <ArrowRight className="w-4 h-4" data-id="element-86" />
              </Link>
            </div> : <div className="text-center py-4" data-id="element-87">
              <p className="text-sm text-gray-400" data-id="element-88">
                Select a volume to see your estimate
              </p>
              <Link to="/calculator" className="inline-flex items-center gap-1 text-sm font-semibold mt-2 hover:underline" style={{
            color: '#1B8F4B'
          }} data-id="element-89">
                Use full calculator <ArrowRight className="w-3 h-3" data-id="element-90" />
              </Link>
            </div>}
        </div>
      </div>
    </section>;
}